import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faUpload,
  faEye,
  faCheckCircle,
  faImage,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

import { formatDate, formatDateTime } from '../../../utils';

/**
 * Sección de evidencias y fotos adjuntas a una incidencia
 */
const TagEvidenceSection = ({
  photosList = [],
  isLoadingPhotos = false,
  isUploadingPhoto = false,
  photoDescription = '',
  setPhotoDescription,
  selectedPhotoPreview = null,
  onDiscardPhotoPreview,
  onSavePhotoPreview,
  isUploadOpen = false,
  setIsUploadOpen,
  fileInputRef,
  onFileSelect,
  onOpenLightbox,
  onOpenDeletePhoto,
  isDark = false
}) => {
  return (
    <div className="pt-2 border-top" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
      {/* Header con botón para subir foto */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCamera} className="text-primary me-1" />
          <span className="fw-bold small" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
            Fotos y Evidencias ({photosList.length})
          </span>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary rounded-pill px-3 shadow-none d-flex align-items-center gap-1.5"
          style={{ fontSize: '0.8rem' }}
          onClick={() => {
            if (isUploadOpen) {
              onDiscardPhotoPreview?.();
              setIsUploadOpen(false);
            } else {
              setIsUploadOpen(true);
            }
          }}
        >
          <span>{isUploadOpen ? 'Cancelar' : 'Subir Foto'}</span>
        </button>
      </div>

      {/* Formulario y Previsualización de Subida */}
      {isUploadOpen && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onFileSelect}
            disabled={isUploadingPhoto}
          />

          {!selectedPhotoPreview ? (
            /* Paso 1: Selector de archivo */
            <div
              className="p-3 rounded-3 border mb-3 text-center"
              style={{
                backgroundColor: isDark ? '#101c26' : '#f8fafc',
                borderColor: isDark ? '#233142' : '#cbd5e1',
                borderStyle: 'dashed'
              }}
            >
              <FontAwesomeIcon icon={faCamera} className="text-primary mb-2" style={{ fontSize: '1.6rem' }} />
              <div className="small fw-semibold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                Selecciona una foto o captura de pantalla
              </div>
              <div className="text-muted mb-2.5" style={{ fontSize: '0.74rem' }}>
                Formatos admitidos: PNG, JPG, JPEG, WEBP (Máx. 10 MB)
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-none fw-semibold"
                onClick={() => fileInputRef.current?.click()}
              >
                <span>Elegir Imagen</span>
              </button>
            </div>
          ) : (
            /* Paso 2: Vista Previa y Confirmación */
            <div
              className="p-3 rounded-3 border mb-3"
              style={{
                backgroundColor: isDark ? '#101c26' : '#f8fafc',
                borderColor: isDark ? '#233142' : '#cbd5e1'
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold d-flex align-items-center gap-1.5" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  <FontAwesomeIcon icon={faEye} className="text-primary me-1" />
                  <span>Vista previa de la evidencia</span>
                </span>
                <span
                  className="badge rounded-pill text-muted fw-normal"
                  style={{ fontSize: '0.72rem', backgroundColor: isDark ? '#1e293b' : '#e2e8f0' }}
                >
                  {selectedPhotoPreview.fileName} · {Math.round(selectedPhotoPreview.fileSize / 1024)} KB
                </span>
              </div>

              <div
                className="rounded-2 overflow-hidden mb-2.5 d-flex align-items-center justify-content-center border"
                style={{
                  maxHeight: '220px',
                  backgroundColor: isDark ? '#0b1319' : '#0000000a',
                  borderColor: isDark ? '#1e2c3a' : '#e2e8f0'
                }}
              >
                <img
                  src={selectedPhotoPreview.dataUrl}
                  alt="Vista previa"
                  style={{
                    maxHeight: '210px',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>

              <div className="mb-2.5">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{
                    backgroundColor: isDark ? '#182635' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#cbd5e1',
                    color: isDark ? '#f9fafd' : '#0f172a',
                    fontSize: '0.82rem'
                  }}
                  placeholder="Nota o descripción de la imagen (opcional)..."
                  maxLength={200}
                  value={photoDescription}
                  onChange={e => setPhotoDescription(e.target.value)}
                  disabled={isUploadingPhoto}
                />
              </div>

              <div className="d-flex align-items-center justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3 shadow-none"
                  onClick={onDiscardPhotoPreview}
                  disabled={isUploadingPhoto}
                >
                  Descartar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-success rounded-pill px-3.5 shadow-none fw-semibold d-flex align-items-center gap-1.5"
                  onClick={onSavePhotoPreview}
                  disabled={isUploadingPhoto}
                  style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                >
                  {isUploadingPhoto ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      <span>Guardar Foto</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Listado de Fotos */}
      {isLoadingPhotos ? (
        <div className="text-center py-4 text-muted">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          <span className="small">Cargando evidencias...</span>
        </div>
      ) : photosList.length === 0 ? (
        <div
          className="p-4 rounded-3 border text-center"
          style={{
            backgroundColor: isDark ? '#101c26' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <FontAwesomeIcon icon={faImage} className="text-muted opacity-50 mb-2" style={{ fontSize: '1.8rem' }} />
          <p className="small text-muted mb-0" style={{ fontSize: '0.82rem' }}>
            No hay fotos ni evidencias adjuntas en esta incidencia.
          </p>
        </div>
      ) : (
        <div className="row g-3">
          {photosList.map(photo => (
            <div key={photo.idIncidenciaFoto} className="col-6 col-md-4 col-lg-3">
              <div
                className="rounded-3 border overflow-hidden h-100 d-flex flex-column"
                style={{
                  backgroundColor: isDark ? '#101c26' : '#ffffff',
                  borderColor: isDark ? '#233142' : '#e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <div
                  style={{
                    height: '110px',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: isDark ? '#0b1319' : '#f1f5f9',
                    overflow: 'hidden'
                  }}
                  onClick={() => onOpenLightbox(photo)}
                >
                  <img
                    src={photo.vUrlFoto}
                    alt={photo.vNombreArchivo || 'Evidencia'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem'
                    }}
                    title="Ver en pantalla completa"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </div>
                </div>

                <div className="p-2 d-flex flex-column justify-content-between flex-grow-1">
                  <div>
                    <div
                      className="small fw-semibold text-truncate mb-0.5"
                      style={{ fontSize: '0.76rem', color: isDark ? '#f9fafd' : '#0f172a' }}
                      title={photo.vNombreArchivo || 'Foto'}
                    >
                      {photo.vNombreArchivo || 'Foto adjunta'}
                    </div>
                    {photo.vDescripcion && (
                      <div
                        className="small text-muted text-truncate mb-1"
                        style={{ fontSize: '0.7rem' }}
                        title={photo.vDescripcion}
                      >
                        {photo.vDescripcion}
                      </div>
                    )}
                  </div>

                  <div className="d-flex align-items-center justify-content-between pt-1 border-top mt-1" style={{ borderColor: isDark ? '#1e2c3a' : '#e2e8f0' }}>
                    <span className="text-muted" style={{ fontSize: '0.68rem' }} title={formatDateTime(photo.dCreatedDate)}>
                      {formatDate(photo.dCreatedDate)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm p-0 text-danger border-0 bg-transparent hover-opacity"
                      title="Eliminar foto"
                      onClick={() => onOpenDeletePhoto(photo)}
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.75rem' }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagEvidenceSection;
